import { useEffect, useMemo } from 'react';

import { Theme } from '@constants';
import { useGetAlertTile } from '@hooks';

// These are product keys used in the v2 Warning API calls.
export const AlertProductKey = {
  marine: 607,
  severe: 608,
  flood: 609,
  winter: 610,
  other: 611,
  tropical: 612,
};

export const AlertType = {
  heatAlert: 'heatAlert',
  winterWeather: 'winterWeather',
  windChill: 'windChill',
  highWind: 'highWind',
  floodWatch: 'floodWatch',
  floodWarning: 'floodWarning',
  flashFloodWarning: 'flashFloodWarning',
  denseFog: 'denseFog',
  tornadoWarning: 'tornadoWarning',
  tornadoWatch: 'tornadoWatch',
  thunderstormWarning: 'thunderstormWarning',
  thunderstormWatch: 'thunderstormWatch',
};

const AlertTypeDescriptions = {
  'Excessive Heat Warning': AlertType.heatAlert,
  'Excessive Heat Watch': AlertType.heatAlert,
  'Heat Advisory': AlertType.heatAlert,

  'Blizzard Warning': AlertType.winterWeather,
  'Ice Storm Warning': AlertType.winterWeather,
  'Lake Effect Snow Warning': AlertType.winterWeather,
  'Snow Squall Warning': AlertType.winterWeather,
  'Winter Storm Warning': AlertType.winterWeather,
  'Winter Storm Watch': AlertType.winterWeather,
  'Winter Weather Advisory': AlertType.winterWeather,

  'Wind Chill Advisory': AlertType.windChill,
  'Wind Chill Warning': AlertType.windChill,
  'Wind Chill Watch': AlertType.windChill,

  'Hurricane Force Wind Watch': AlertType.highWind,
  'Hurricane Force Wind Warning': AlertType.highWind,
  'Brisk Wind Advisory': AlertType.highWind,
  'Extreme Wind Warning': AlertType.highWind,
  'High Wind Warning': AlertType.highWind,
  'High Wind Watch': AlertType.highWind,
  'Lake Wind Advisory': AlertType.highWind,
  'Wind Advisory': AlertType.highWind,

  'Coastal Flood Watch': AlertType.floodWatch,
  'Lakeshore Flood Watch': AlertType.floodWatch,
  'Flood Watch': AlertType.floodWatch,

  'Coastal Flood Warning': AlertType.floodWarning,
  'Lakeshore Flood Warning': AlertType.floodWarning,
  'Flood Warning': AlertType.floodWarning,

  'Flash Flood Warning': AlertType.flashFloodWarning,
  'Dense Fog Advisory': AlertType.denseFog,
  'Tornado Warning': AlertType.tornadoWarning,
  'Tornado Watch': AlertType.tornadoWatch,
  'Severe Thunderstorm Warning': AlertType.thunderstormWarning,
  'Severe Thunderstorm Watch': AlertType.thunderstormWatch,
};

const AlertTypeStrokeColor = {
  [AlertType.flashFloodWarning]: '#25ea2a',
  [AlertType.tornadoWarning]: '#c9092b',
  [AlertType.thunderstormWarning]: '#e1cc2c',
};

const AlertTypeFillColor = {
  [AlertType.heatAlert]: '#f636cd',
  [AlertType.winterWeather]: '#1aa5c7',
  [AlertType.windChill]: '#09519c',
  [AlertType.highWind]: '#9058fa',
  [AlertType.floodWatch]: '#25ea2a',
  [AlertType.floodWarning]: '#19710f',
  [AlertType.denseFog]: '#626364',
  [AlertType.tornadoWatch]: '#c9092b',
  [AlertType.thunderstormWatch]: '#e1cc2c',
};

// Returns an SVG path string for the specified coordinateGroup.
const svgPathString = (coordinateGroup, mercatorMap) => {
  let pathString = '';

  if (coordinateGroup != null && coordinateGroup.length > 0) {
    for (const [coordinateIndex, coordinate] of coordinateGroup.entries()) {
      const coordinateLon = coordinate[0];
      const coordinateLat = coordinate[1];

      let [x, y] = mercatorMap.coordsToViewPoint(coordinateLon, coordinateLat);

      x /= Theme.pixelRatio();
      y /= Theme.pixelRatio();

      pathString += coordinateIndex === 0 ? ` M${x} ${y}` : ` L${x} ${y}`;
    }
  }

  return `${pathString} Z`;
};

export const MapAlertTile = ({ productKey, showAlertTypes, time, x, y, z, mercatorMap, onTileLoaded }) => {
  let { data: alertTileData } = useGetAlertTile({
    productKey,
    time,
    x,
    y,
    z,
  });

  const alertPolygons = useMemo(() => {
    const paths = [];
    const validGeometryTypes = new Set(['Polygon', 'MultiPolygon']);

    if (!alertTileData?.features) {
      return null;
    }

    for (const feature of alertTileData.features) {
      let strokeColor = null;
      let fillColor = null;

      if (feature.properties != null && feature.properties?.eventDescription != null) {
        const alertType = AlertTypeDescriptions[feature.properties.eventDescription];

        if (alertType != null && showAlertTypes.includes(alertType)) {
          strokeColor = AlertTypeStrokeColor[alertType];
          fillColor = AlertTypeFillColor[alertType];
        }
      }

      if (
        feature.geometry == null ||
        !validGeometryTypes.has(feature.geometry.type) ||
        feature.geometry.coordinates == null ||
        feature.geometry.coordinates.length === 0
      ) {
        continue;
      }

      if (strokeColor === null && fillColor === null) {
        continue;
      }

      if (feature.geometry.type === 'Polygon') {
        for (const [coordinateGroupIndex, coordinateGroup] of feature.geometry.coordinates.entries()) {
          paths.push({
            strokeColor,
            fillColor,
            id: `${feature.properties.detailKey}-${coordinateGroupIndex}`,
            severityCode: feature.properties.severityCode,
            svgPath: svgPathString(coordinateGroup, mercatorMap),
          });
        }
      } else if (feature.geometry.type === 'MultiPolygon') {
        for (const [coordinateOuterGroupIndex, coordinateOuterGroup] of feature.geometry.coordinates.entries()) {
          for (const [coordinateGroupIndex, coordinateGroup] of coordinateOuterGroup.entries()) {
            paths.push({
              strokeColor,
              fillColor,
              id: `${feature.properties.detailKey}-${coordinateOuterGroupIndex}-${coordinateGroupIndex}`,
              severityCode: feature.properties.severityCode,
              svgPath: svgPathString(coordinateGroup, mercatorMap),
            });
          }
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    alertTileData = null;

    return paths;
  }, [alertTileData]);

  useEffect(() => {
    if (alertPolygons != null) {
      onTileLoaded(alertPolygons);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertPolygons]);

  // Don't render anything
  return null;
};

// MapAlertTile.propTypes = {
//   x: PropTypes.number.isRequired,
//   y: PropTypes.number.isRequired,
//   z: PropTypes.number.isRequired,
//   time: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
//   productKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
//   // Called with an array of objects once data loads.  The objects have the following properties:
//   //   strokeColor:  An HTML color, or undefined if the path should not be stroked.
//   //   fillColor:    An HTML color, or undefined if the path should not be filled.
//   //   path:         An array of [lon, lat] pairs.
//   onTileLoaded: PropTypes.func,
//   mercatorMap: PropTypes.object,
//   showAlertTypes: PropTypes.arrayOf(PropTypes.oneOf(Object.values(AlertType))),
// };

MapAlertTile.defaultProps = {
  onTileLoaded: () => {},
  mercatorMap: null,
  showAlertTypes: Object.values(AlertType),
};
